/**
 * ERC-8004 Q&A Tab
 * Ask questions about registered ERC-8004 agents using the knowledge graph
 */

import { useState } from 'react';
import { useAccount } from 'wagmi';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ErrorMessage } from '@/components/shared/ErrorMessage';
import { AgentCard } from '@/components/agent/AgentCard';
import { TEEAttestation } from '../TEEAttestation';
import type { TenantCredentials } from '@/types/database';

const AGENT_SERVICE_URL = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:8080';

interface GraphQAResponse {
  answer: string;
  reasoning: string;
  sources: any[];
  confidence: number;
  graph_stats: {
    nodes_used: number;
    edges_used: number;
  };
  tee_proof?: any;
  proof_id?: string;
}

interface QATabProps {
  credentials: TenantCredentials | null;
  onProvision: () => void;
}

export function QATab({ credentials, onProvision }: QATabProps) {
  const { address } = useAccount();
  const [question, setQuestion] = useState('What agents provide DeFi data?');
  const [answer, setAnswer] = useState<GraphQAResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAsk = async () => {
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setIsLoading(true);
    setError(null);
    setAnswer(null);

    try {
      const response = await fetch(`${AGENT_SERVICE_URL}/api/graph-qa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question.trim(),
          max_nodes: 50,
          wallet_address: address || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      const data: GraphQAResponse = await response.json();
      setAnswer(data);
    } catch (err) {
      console.error('Q&A request failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to get answer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  // If no credentials, show provision prompt
  if (!credentials) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <div className="mb-6">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Database Provisioned</h3>
        <p className="text-gray-600 mb-6">
          You need to provision a KnowledgeFlowDB database first to use the Q&A feature.
        </p>
        <button
          onClick={onProvision}
          className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          Provision Database
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg
            className="h-5 w-5 text-blue-600 mt-0.5 mr-3"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-blue-900">ERC-8004 Agent Q&A</h3>
            <p className="mt-1 text-sm text-blue-700">
              Ask questions about registered ERC-8004 agents. The system uses your KnowledgeFlowDB
              database to answer questions with verifiable TEE proofs.
            </p>
          </div>
        </div>
      </div>

      {/* Question Input */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
          Ask a Question
        </label>
        <div className="space-y-4">
          <textarea
            id="question"
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="What agents provide DeFi data?"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <button
            onClick={handleAsk}
            disabled={isLoading || !question.trim()}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isLoading ? 'Searching...' : 'Ask Question'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm p-12">
          <LoadingSpinner />
          <p className="text-center text-gray-600 mt-4">Querying knowledge graph...</p>
        </div>
      )}

      {/* Error State */}
      {error && <ErrorMessage message={error} />}

      {/* Answer Display */}
      {answer && (
        <div className="space-y-6">
          {/* Answer Card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Answer</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Confidence:</span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    answer.confidence >= 0.8
                      ? 'bg-green-100 text-green-800'
                      : answer.confidence >= 0.6
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {(answer.confidence * 100).toFixed(0)}%
                </span>
              </div>
            </div>
            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{answer.answer}</p>

            {answer.reasoning && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Reasoning</h4>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{answer.reasoning}</p>
              </div>
            )}

            {/* Graph Stats */}
            {answer.graph_stats && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Graph Statistics</h4>
                <div className="flex space-x-6 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Nodes:</span> {answer.graph_stats.nodes_used}
                  </div>
                  <div>
                    <span className="font-medium">Edges:</span> {answer.graph_stats.edges_used}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Source Agents */}
          {answer.sources && answer.sources.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Source Agents ({answer.sources.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {answer.sources.map((agent, idx) => (
                  <AgentCard key={idx} agent={agent} />
                ))}
              </div>
            </div>
          )}

          {/* TEE Proof */}
          {answer.tee_proof && (
            <TEEAttestation proof={answer.tee_proof} proofId={answer.proof_id} />
          )}
        </div>
      )}

      {/* Example Questions */}
      {!answer && !isLoading && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Example Questions</h3>
          <div className="space-y-2">
            {[
              'What agents provide DeFi data?',
              'Which agents support smart contract analysis?',
              'Show me agents that analyze blockchain transactions',
              'What agents are available for NFT analytics?',
            ].map((exampleQ) => (
              <button
                key={exampleQ}
                onClick={() => setQuestion(exampleQ)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 rounded-md transition-colors"
              >
                {exampleQ}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
